import React, { useState, useEffect } from 'react';
import './AiReviewDialog.css';
import { fetchAiReview, prepareReviewDataFromSimulated } from './services/aiReviewService';
import {
  getEnrichedTrades as apiGetEnriched,
  getSimulatedResultsRange as apiGetSimRange,
  listReviews as apiListReviews,
} from './services/apiService';
import { getETNow, getETDateString } from './utils/marketUtils';
import { isActualEntry } from './utils/tradeUtils';
import ReviewDetailDialog from './ReviewDetailDialog';

const DEFAULT_REVIEW_LIST_LIMIT = 50;

const timeRangeLabel = (tr) => {
  const labels = { day: 'Daily', week: 'Weekly', month: 'Monthly', all: 'All', custom: 'Custom' };
  return labels[tr] || tr;
};

/**
 * AI Review Dialog Component
 *
 * Two functions:
 *   1) Generate Review — analyze recent trades; preview opens in a stacked
 *      ReviewDetailDialog with [Save Review] (preview→save, no auto-save).
 *   2) Browse existing reviews — list reviews in the current date range,
 *      click [View] to open them in a stacked ReviewDetailDialog (read-only).
 *
 * The detail rendering lives in ReviewDetailDialog so AI PROFILE can reuse it.
 */
function AiReviewDialog({ onClose, initialDates }) {
  const [timeRange, setTimeRange] = useState('day');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dataPreview, setDataPreview] = useState(null);

  const [existingReviews, setExistingReviews] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [generatedDetail, setGeneratedDetail] = useState(null);
  const [viewingReviewId, setViewingReviewId] = useState(null);

  useEffect(() => {
    if (initialDates) {
      setTimeRange('custom');
      setCustomStartDate(initialDates.start);
      setCustomEndDate(initialDates.end);
    }
  }, [initialDates]);

  useEffect(() => {
    if (isAnalyzing) return;
    if (timeRange === 'custom' && (!customStartDate || !customEndDate)) return;
    const loadPreview = async () => {
      try {
        const [enriched, simulated] = await Promise.all([
          loadEnrichedData(timeRange).catch(() => []),
          loadSimulatedSummary(timeRange).catch(() => null)
        ]);
        const simEntered = simulated?.trades?.length || 0;
        const simTotal = simulated?.totalTradeCount || simEntered;
        const simScans = simulated ? new Set(simulated.trades?.map(t => t.scanId)).size : 0;

        const now = getETNow();
        const { startDate, endDate } = getDateRange(timeRange, now);
        const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

        const simDates = simulated?.trades
          ? [...new Set(simulated.trades.map(t => t.simDate || t.date).filter(Boolean))].sort()
          : [];
        const simDateRange = simDates.length > 0
          ? (simDates.length === 1
            ? new Date(simDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : `${new Date(simDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(simDates[simDates.length - 1] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)
          : '';

        const enrichedDates = enriched
          ? [...new Set(enriched.map(e => e.tradeDate || e.date).filter(Boolean))].sort()
          : [];
        const enrichedDateRange = enrichedDates.length > 0
          ? (enrichedDates.length === 1
            ? new Date(enrichedDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : `${new Date(enrichedDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(enrichedDates[enrichedDates.length - 1] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)
          : '';

        setDataPreview({
          enrichedCount: enriched?.length || 0,
          simEnteredCount: simEntered,
          simTotalCount: simTotal,
          simScanCount: simScans,
          dateRangeStr: `${fmtDate(startDate)} - ${fmtDate(endDate)}`,
          simDateRange,
          enrichedDateRange,
        });
      } catch {
        setDataPreview(null);
      }
    };
    loadPreview();
  }, [timeRange, customStartDate, customEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (timeRange === 'custom' && (!customStartDate || !customEndDate)) {
      setExistingReviews([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const now = getETNow();
        const { startDate, endDate } = getDateRange(timeRange, now);
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const reviews = await apiListReviews(fmt(startDate), fmt(endDate));
        if (cancelled) return;
        const sorted = Array.isArray(reviews)
          ? [...reviews].sort((a, b) => (b.reviewDate || '').localeCompare(a.reviewDate || '') || (b.id - a.id))
          : [];
        setExistingReviews(sorted);
        setShowAll(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load existing reviews:', err);
        setExistingReviews([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [timeRange, customStartDate, customEndDate, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    setIsAnalyzing(true);
    setLoadError(null);

    try {
      const [enrichedRecords, simulatedSummary] = await Promise.all([
        loadEnrichedData(timeRange).catch(() => []),
        loadSimulatedSummary(timeRange).catch(() => null)
      ]);

      const hasEnriched = enrichedRecords && enrichedRecords.length > 0;
      const hasSimulated = simulatedSummary && simulatedSummary.trades && simulatedSummary.trades.length > 0;

      if (!hasEnriched && !hasSimulated) {
        setLoadError('No trade data found. Please run Backtest or Auto-Match in Trading Journal first.');
        setIsAnalyzing(false);
        return;
      }

      let data;
      let dataSource;

      if (hasEnriched) {
        data = prepareReviewDataFromEnriched(enrichedRecords, timeRange);
        dataSource = hasSimulated ? 'mixed' : 'real';
      } else {
        data = prepareReviewDataFromSimulated(simulatedSummary.trades, timeRange, simulatedSummary.totalTradeCount);
        dataSource = 'simulated';
      }

      data.dataSource = dataSource;

      if (data.pairs.length === 0) {
        setLoadError('No completed trade pairs found in the selected time range.');
        setIsAnalyzing(false);
        return;
      }

      const reviewDataForBackend = { ...data };
      const results = await fetchAiReview(reviewDataForBackend);

      setGeneratedDetail({
        anthropicResult: results.anthropic,
        openaiResult: results.openai,
        extractedProfile: results.extractedProfile,
        reviewMeta: {
          reviewDate: getETDateString(),
          timeRange,
          dateStart: data.dateRange?.start,
          dateEnd: data.dateRange?.end,
        },
      });
      setIsAnalyzing(false);
    } catch (error) {
      console.error('Error loading trade data:', error);
      setLoadError(`Failed to load trade data: ${error.message}`);
      setIsAnalyzing(false);
    }
  };

  const loadEnrichedData = async (timeRange) => {
    const now = getETNow();
    const { startDate, endDate } = getDateRange(timeRange, now);
    const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

    const resp = await apiGetEnriched(startStr, endStr);
    const items = Array.isArray(resp) ? resp : [];

    const records = [];
    for (const item of items) {
      const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
      if (data?.enrichedPairs) {
        data.enrichedPairs.forEach(pair => {
          const entryDate = new Date(pair.entry.uiRecord.timestamp);
          if (entryDate >= startDate && entryDate <= endDate) {
            records.push(pair);
          }
        });
      }
    }
    return records;
  };

  const getDateRange = (timeRange, now) => {
    const endDate = new Date(now);
    const startDate = new Date(now);

    switch (timeRange) {
      case 'day':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'all':
        startDate.setFullYear(2020, 0, 1);
        break;
      case 'custom':
        return {
          startDate: new Date(customStartDate + 'T00:00:00'),
          endDate: new Date(customEndDate + 'T23:59:59')
        };
      default:
        startDate.setDate(now.getDate() - 1);
    }

    return { startDate, endDate };
  };

  const loadSimulatedSummary = async (timeRange) => {
    const now = getETNow();
    const { startDate, endDate } = getDateRange(timeRange, now);
    const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

    const resp = await apiGetSimRange(startDateStr, endDateStr);
    const items = Array.isArray(resp) ? resp : [];
    const allTrades = [];
    for (const item of items) {
      const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
      const trades = Array.isArray(data) ? data : data?.trades;
      if (Array.isArray(trades)) allTrades.push(...trades);
    }
    if (allTrades.length === 0) return null;

    const enteredTrades = allTrades.filter(isActualEntry);
    return {
      trades: enteredTrades,
      totalTradeCount: allTrades.length,
    };
  };

  const prepareReviewDataFromEnriched = (enrichedPairs, timeRange) => {
    if (!enrichedPairs || enrichedPairs.length === 0) {
      return {
        timeRange,
        dateRange: { start: getETDateString(), end: getETDateString() },
        stats: { totalTrades: 0, entries: 0, exits: 0, aiRecommended: 0 },
        pairs: []
      };
    }

    const entries = enrichedPairs.length;
    const exits = enrichedPairs.length;
    const aiRecommended = enrichedPairs.filter(pair =>
      pair.entry.uiRecord.aiRecommendations?.claudeRecommended ||
      pair.entry.uiRecord.aiRecommendations?.gptRecommended
    ).length;

    const reviewPairs = enrichedPairs.map(pair => ({
      symbol: pair.symbol,
      quantity: pair.quantity,
      entryPrice: pair.entryPrice,
      exitPrice: pair.exitPrice,
      profit: pair.profit,
      profitPercent: pair.profitPercent,
      fees: pair.fees || 0,
      duration: pair.duration,
      entryTimestamp: pair.entry.uiRecord.timestamp,
      exitTimestamp: pair.exit.uiRecord.timestamp,
      aiRecommendations: {
        claudeRecommended: pair.entry.uiRecord.aiRecommendations?.claudeRecommended || false,
        claudeRating: pair.entry.uiRecord.aiRecommendations?.claudeRating || null,
        claudeRank: pair.entry.uiRecord.aiRecommendations?.claudeRank || null,
        claudeTrend: pair.entry.uiRecord.aiRecommendations?.claudeTrend || null,
        claudePattern: pair.entry.uiRecord.aiRecommendations?.claudePattern || null,
        claudeAction: pair.entry.uiRecord.aiRecommendations?.claudeAction || null,
        claudeMarketSummary: pair.entry.uiRecord.aiRecommendations?.claudeMarketSummary || null,
        claudeMarketAnalysis: pair.entry.uiRecord.aiRecommendations?.claudeMarketAnalysis || null,
        gptRecommended: pair.entry.uiRecord.aiRecommendations?.gptRecommended || false,
        gptRating: pair.entry.uiRecord.aiRecommendations?.gptRating || null,
        gptRank: pair.entry.uiRecord.aiRecommendations?.gptRank || null,
        gptTrend: pair.entry.uiRecord.aiRecommendations?.gptTrend || null,
        gptPattern: pair.entry.uiRecord.aiRecommendations?.gptPattern || null,
        gptAction: pair.entry.uiRecord.aiRecommendations?.gptAction || null,
        gptMarketSummary: pair.entry.uiRecord.aiRecommendations?.gptMarketSummary || null,
        gptMarketAnalysis: pair.entry.uiRecord.aiRecommendations?.gptMarketAnalysis || null
      },
      entryContext: {
        marketSentiment: pair.entry.uiRecord.snapshot?.marketSentiment || null,
        indices: pair.entry.uiRecord.snapshot?.indices || {},
        score: pair.entry.uiRecord.snapshot?.score || null,
        dailyGain: pair.entry.uiRecord.snapshot?.dailyGain || null,
        volume: pair.entry.uiRecord.snapshot?.volume || null,
        accelerationArrow: pair.entry.uiRecord.snapshot?.accelerationArrow || null
      },
      exitContext: {
        marketSentiment: pair.exit.uiRecord.snapshot?.marketSentiment || null,
        indices: pair.exit.uiRecord.snapshot?.indices || {},
        score: pair.exit.uiRecord.snapshot?.score || null,
        dailyGain: pair.exit.uiRecord.snapshot?.dailyGain || null,
        volume: pair.exit.uiRecord.snapshot?.volume || null,
        accelerationArrow: pair.exit.uiRecord.snapshot?.accelerationArrow || null
      }
    }));

    const timestamps = enrichedPairs.map(p => new Date(p.entry.uiRecord.timestamp));
    const minDate = new Date(Math.min(...timestamps));
    const maxDate = new Date(Math.max(...timestamps));

    return {
      timeRange,
      dateRange: {
        start: minDate.toISOString().split('T')[0],
        end: maxDate.toISOString().split('T')[0]
      },
      stats: {
        totalTrades: entries + exits,
        entries,
        exits,
        aiRecommended
      },
      pairs: reviewPairs
    };
  };

  const visibleReviews = showAll
    ? existingReviews
    : existingReviews.slice(0, DEFAULT_REVIEW_LIST_LIMIT);

  return (
    <>
      <div className="ai-review-overlay" onClick={onClose}>
        <div className="ai-review-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="ai-review-header">
            <h2>🤖 AI Review</h2>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>

          <div className="ai-review-toolbar">
            <div className="time-range-selector">
              <button className={timeRange === 'day' ? 'active' : ''} onClick={() => setTimeRange('day')}>Today</button>
              <button className={timeRange === 'week' ? 'active' : ''} onClick={() => setTimeRange('week')}>7 Days</button>
              <button className={timeRange === 'month' ? 'active' : ''} onClick={() => setTimeRange('month')}>30 Days</button>
              <button className={timeRange === 'all' ? 'active' : ''} onClick={() => setTimeRange('all')}>All</button>
              <button className={timeRange === 'custom' ? 'active' : ''} onClick={() => setTimeRange('custom')}>Custom</button>

              {timeRange === 'custom' && (
                <div className="custom-date-range">
                  <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} />
                  <span>~</span>
                  <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} />
                </div>
              )}
            </div>

            <button className="analyze-btn" onClick={handleGenerate} disabled={isAnalyzing}>
              {isAnalyzing ? '⏳ Analyzing...' : '🔍 Generate Review'}
            </button>
          </div>

          <div className="ai-review-content">
            {loadError && !isAnalyzing && (
              <div className="empty-state">
                <div className="empty-icon">⚠️</div>
                <p style={{ color: '#dc2626' }}>{loadError}</p>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '12px' }}>
                  Tip: Run "Auto-Match" in Trading Journal to generate enriched data
                </p>
              </div>
            )}

            {!isAnalyzing && !loadError && (
              <>
                {dataPreview ? (
                  <div className="data-preview">
                    <h4>Data Available ({dataPreview.dateRangeStr})</h4>
                    <div className="preview-item">
                      <span className="preview-label">Simulated Trades:</span>
                      <span className="preview-value">
                        {dataPreview.simEnteredCount > 0
                          ? `${dataPreview.simEnteredCount}/${dataPreview.simTotalCount} trades (${dataPreview.simScanCount} ${dataPreview.simScanCount === 1 ? 'scan' : 'scans'})${dataPreview.simDateRange ? ` · ${dataPreview.simDateRange}` : ''}`
                          : 'None — run Backtest first'}
                      </span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Real Trades:</span>
                      <span className="preview-value">
                        {dataPreview.enrichedCount > 0
                          ? `${dataPreview.enrichedCount} pairs${dataPreview.enrichedDateRange ? ` · ${dataPreview.enrichedDateRange}` : ''}`
                          : 'None — run Auto-Match to add'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="data-preview-placeholder">
                    Loading data...
                  </div>
                )}

                <div className="existing-reviews-section">
                  <h4>📥 Existing Reviews ({existingReviews.length})</h4>
                  {existingReviews.length === 0 ? (
                    <p className="empty-list">No saved reviews in this date range.</p>
                  ) : (
                    <>
                      <ul className="existing-reviews-list">
                        {visibleReviews.map(r => (
                          <li key={r.id} className="existing-review-item">
                            <span className="existing-review-meta">
                              <strong>{timeRangeLabel(r.timeRange)} Review #{r.id}</strong>
                              <span className="existing-review-date"> · {r.reviewDate}</span>
                              {r.dateStart && r.dateEnd && r.dateStart !== r.dateEnd && (
                                <span className="existing-review-range"> · {r.dateStart} → {r.dateEnd}</span>
                              )}
                            </span>
                            <button
                              className="view-review-btn"
                              onClick={() => setViewingReviewId(r.id)}
                              title="View review details"
                            >
                              View
                            </button>
                          </li>
                        ))}
                      </ul>
                      {existingReviews.length > DEFAULT_REVIEW_LIST_LIMIT && (
                        <button
                          className="show-all-btn"
                          onClick={() => setShowAll(!showAll)}
                        >
                          {showAll
                            ? `Show fewer (first ${DEFAULT_REVIEW_LIST_LIMIT})`
                            : `Show all (${existingReviews.length})`}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {isAnalyzing && (
              <div className="loading-state">
                <div className="loading-icon">⏳</div>
                <h3>Analyzing Your Trades...</h3>
                <p>AI is reviewing your decision quality, patterns, and outcomes</p>
              </div>
            )}
          </div>

          <div className="ai-review-footer">
            <div className="footer-buttons">
              <button className="btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {viewingReviewId && (
        <ReviewDetailDialog
          reviewId={viewingReviewId}
          onClose={() => setViewingReviewId(null)}
        />
      )}

      {generatedDetail && (
        <ReviewDetailDialog
          generatedData={generatedDetail}
          onSaved={() => setRefreshKey(k => k + 1)}
          onClose={() => setGeneratedDetail(null)}
        />
      )}
    </>
  );
}

export default AiReviewDialog;
